import React from 'react';
import {
  Globe, Phone, Calendar, Bot, Newspaper, Mail, MapPin, FileText,
  CreditCard, MessageCircle, Users, Bell, Shield, BookOpen,
  GraduationCap, Award, Clock, Camera, Heart, Settings,
  Video, Music, Trophy, Compass
} from 'lucide-react';

// The single source of truth for the built-in icon library.
// A tile stores `icon = "lucide:calendar"` etc.
// Anything with the "lucide:" prefix is rendered from this map.
export const ICON_LIBRARY = [
  // Common
  { key: 'globe', label: 'Website', component: Globe, group: 'Common' },
  { key: 'phone', label: 'Call us', component: Phone, group: 'Common' },
  { key: 'calendar', label: 'Calendar', component: Calendar, group: 'Common' },
  { key: 'bot', label: 'Chat / Bot', component: Bot, group: 'Common' },
  { key: 'newspaper', label: 'News', component: Newspaper, group: 'Common' },
  { key: 'mail', label: 'Email', component: Mail, group: 'Common' },
  { key: 'map-pin', label: 'Location', component: MapPin, group: 'Common' },
  { key: 'bell', label: 'Notifications', component: Bell, group: 'Common' },

  // Business
  { key: 'file-text', label: 'Documents', component: FileText, group: 'Business' },
  { key: 'credit-card', label: 'Payments', component: CreditCard, group: 'Business' },
  { key: 'message-circle', label: 'Messages', component: MessageCircle, group: 'Business' },
  { key: 'users', label: 'Community', component: Users, group: 'Business' },
  { key: 'shield', label: 'Policies', component: Shield, group: 'Business' },
  { key: 'settings', label: 'Admin', component: Settings, group: 'Business' },
  { key: 'clock', label: 'Schedule', component: Clock, group: 'Business' },
  { key: 'compass', label: 'Directions', component: Compass, group: 'Business' },

  // Education
  { key: 'book-open', label: 'Library', component: BookOpen, group: 'Education' },
  { key: 'graduation-cap', label: 'Studies', component: GraduationCap, group: 'Education' },
  { key: 'award', label: 'Achievements', component: Award, group: 'Education' },
  { key: 'trophy', label: 'Sports', component: Trophy, group: 'Education' },

  // Media & Wellbeing
  { key: 'camera', label: 'Photos', component: Camera, group: 'Media' },
  { key: 'video', label: 'Videos', component: Video, group: 'Media' },
  { key: 'music', label: 'Music', component: Music, group: 'Media' },
  { key: 'heart', label: 'Wellbeing', component: Heart, group: 'Media' },
];

// Fast lookup by key.
const ICON_MAP = ICON_LIBRARY.reduce((acc, i) => {
  acc[i.key] = i.component;
  return acc;
}, {});

// Some Lucide icons have more built-in whitespace than others, making them
// look visually smaller when rendered at the same size. This map applies a
// per-icon scale so the whole library reads at consistent visual weight.
const VISUAL_SCALE = {
  'bot': 1.15,           // Bot has antennae headroom
  'graduation-cap': 1.05,
  'trophy': 1.05,
  'award': 1.05,
};

/**
 * Renders a lucide library icon from a tile.icon string like "lucide:calendar".
 * Returns null if the icon string doesn't reference the library.
 */
export function LibraryIcon({ iconString, className = '', strokeWidth = 1.5, color }) {
  if (typeof iconString !== 'string' || !iconString.startsWith('lucide:')) return null;
  const key = iconString.slice('lucide:'.length);
  const Icon = ICON_MAP[key];
  if (!Icon) {
    // Fallback so a missing/typo icon doesn't leave a blank tile.
    return (
      <svg viewBox="0 0 24 24" className={className}
           fill="none" stroke={color || '#94A3B8'}
           strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
    );
  }
  const scale = VISUAL_SCALE[key];
  const inner = <Icon className={className} strokeWidth={strokeWidth} color={color || '#4B5563'} />;
  if (!scale) return inner;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
        transform: `scale(${scale})`,
      }}
    >
      {inner}
    </span>
  );
}

export function isLibraryIcon(iconString) {
  return typeof iconString === 'string' && iconString.startsWith('lucide:');
}
